import {
  useForm,
  useFieldArray,
  Controller,
  FieldErrors,
} from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  CheckCircle,
  CircleAlert,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  SearchX,
  Settings,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import type { DocumentType } from "@/types/form";
import { getValidationSchema } from "@/schema/documentSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { SearchModal } from "./modal-item";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  buyerSchema,
  LineItemForm,
  supplierSchema,
  type invoiceItemSchema,
} from "@/schema/schema";
import { useParams } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";
import {
  RATE_DISPLAY_PRECISION,
  RATE_INPUT_STEP,
  roundTo,
} from "@/utils/decimal";
import { deriveDocumentTotals } from "@/utils/documentTotals";
import { useInvoiceDetails } from "@/hooks/useInvoiceDetails";
import { Badge, Box, Callout, Tabs } from "@radix-ui/themes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { registrationTypes } from "@/constants/registrationTypes";
import { countries } from "@/constants/countryCodes";
import { states } from "@/constants/states";
import { getInvoiceStatusLabel } from "@/constants/invoiceStatusLabels";
import { format } from "date-fns";
import { invoiceDetails } from "@/types/invoiceDetails";
import { frequencyOfBilling } from "@/constants/frequencyOfBilling";
import { paymentMode } from "@/constants/paymentMode";
import { classificationCodes } from "@/constants/classificationCodes";
import { UomCombobox } from "@/components/uom-combobox";
import { taxTypes } from "@/constants/taxTypes";
import { useJobCode } from "@/hooks/userJobCode";
import LoadingIndicator from "@/components/LoadingIndicator";
import { useInvoices } from "@/hooks/useInvoices";
import { useToast } from "@/hooks/use-toast";
import { navigate } from "wouter/use-browser-location";
import TinSearch from "./modal-tin";
import { useEInvoices } from "@/hooks/useEInvoices";
import { useCurrentActiveBranch } from "@/hooks/useCurrentActiveBranch";
import { api } from "@/utils/axiosInterceptor";
import {
  allocateNetByTaxType,
  round2,
  type ConsolidateTaxEntry,
} from "@/utils/consolidationTax";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useQueryClient } from "@tanstack/react-query";
import SearchInvoiceReferenceNumber from "./search-invoice-reference";
import { EInvoice } from "@/types/e-invoice";
import { lineItemDefaultValue } from "@/constants/lineItemDefault";
import SearchSupplierModal from "./modal-supplier";
import SearchBuyerModal from "./modal-buyer";
import InsertEInvoiceReference from "./insert-einvoice-reference";
import SearchInvoiceModal, { InvoiceList } from "./modal-invoice";
import { currencyCodes } from "@/constants/currencyCodes";

const EInvoiceWithDetailsForm = () => {
  const {
    round,
    format: formatDecimal,
    step,
    precision,
    roundTotal,
    formatTotalAmount,
  } = useDecimalPrecision();
  const { toast } = useToast();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const { jobCode } = useJobCode();
  const params = useParams();
  const { user } = useAuth();
  const invoiceId = window.history.state?.id ?? null;
  const documentType = params.documentType as DocumentType;
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const {
    invoiceDetails,
    isLoading,
    error,
    eInvoiceDetails,
    eInvoiceDetailsIsLoading,
  } = useInvoiceDetails(invoiceId);
  const { cancelEInvoice } = useEInvoices();
  const { currentActiveBranch } = useCurrentActiveBranch();
  const {
    updateInvoice,
    isUpdatingInvoice,
    deleteConsolidateItem,
    deleteInvoiceAsync,
  } = useInvoices();
  const [openTinSearch, setOpenTinSearch] = useState<boolean>(false);
  const [openEInvoice, setOpenEInvoice] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState("");
  const [currencySearch, setCurrencySearch] = useState("");
  const [newLineItem, setNewLineItem] =
    useState<LineItemForm>(lineItemDefaultValue);
  const [isSearchBuyerModalOpen, setIsSearchBuyerModalOpen] = useState(false);
  const [isSearchSupplierModalOpen, setIsSearchSupplierModalOpen] =
    useState(false);
  const [supplierCode, setSupplierCode] = useState<string | null>(null);
  const [buyerCode, setBuyerCode] = useState<string | null>(null);
  const [invoiceReferenceSubstitution, setInvoiceReferenceSubstitution] =
    useState<string | undefined>(undefined);
  const [openInsertReference, setOpenInsertReference] =
    useState<boolean>(false);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState<boolean>(true);
  const queryClient = useQueryClient();

  const { zodSchema, defaultValues } = useMemo(() => {
    if (!user || !invoiceDetails) {
      return {
        zodSchema: getValidationSchema(documentType),
        defaultValues: undefined,
      };
    }

    const invoiceDetailsData: invoiceDetails = {
      id: invoiceDetails.id,
      codeNumber: invoiceDetails.codeNumber,
      invoiceDate: format(invoiceDetails.invoiceTime, "yyyy-MM-dd'T'HH:mm"),
      invoiceTime: format(invoiceDetails.invoiceTime, "HH:mm:ss"),
      currencyCode: invoiceDetails.currencyCode,
      typeCode: invoiceDetails.typeCode,
      version: invoiceDetails.version,
      originalInvoiceNumber: invoiceDetails.originalInvoiceNumber ?? undefined,
      originalInvoiceReference: invoiceDetails.originalInvoiceReference ?? [],
      issuerDigitalSignature: invoiceDetails.issuerDigitalSignature,
      currencyExchangeRate: Number(invoiceDetails.currencyExchangeRate),
      frequencyOfBilling: invoiceDetails.frequencyOfBilling,
      billingPeriodStartDate: invoiceDetails.billingPeriodStartDate
        ? format(invoiceDetails.billingPeriodStartDate, "yyyy-MM-dd")
        : null,
      billingPeriodEndDate: invoiceDetails.billingPeriodEndDate
        ? format(invoiceDetails.billingPeriodEndDate, "yyyy-MM-dd")
        : null,
      paymentMode: invoiceDetails.paymentMode,
      supplierBankAccountNumber: invoiceDetails.supplierBankAccountNumber,
      paymentTerms: invoiceDetails.paymentTerms,
      prepaymentAmount: Number(invoiceDetails.prepaymentAmount),
      prepaymentDate: invoiceDetails.prepaymentDate
        ? format(invoiceDetails.prepaymentDate, "yyyy-MM-dd")
        : null,
      // prepaymentTime: format(invoiceDetails.prepaymentTime, "yyyy-MM-dd"),
      prepaymentReferenceNumber: invoiceDetails.prepaymentReferenceNumber,
      billReferenceNumber: invoiceDetails.billReferenceNumber,
      totalExcludingTax: invoiceDetails.totalExcludingTax,
      totalIncludingTax: invoiceDetails.totalIncludingTax,
      totalPayableAmount: invoiceDetails.totalPayableAmount,
      totalNetAmount: invoiceDetails.totalNetAmount,
      totalDiscountValue: invoiceDetails.totalDiscountValue,
      totalFeeChargeAmount: invoiceDetails.totalFeeChargeAmount,
      totalTaxAmount: invoiceDetails.totalTaxAmount,
      roundingAmount: invoiceDetails.roundingAmount,
      totalTaxableAmountPerTaxType: invoiceDetails.totalTaxableAmountPerTaxType,
      totalTaxAmountPerTaxType: invoiceDetails.totalTaxAmountPerTaxType,
      taxType: invoiceDetails.taxType,
      invoiceAdditionalDiscountAmount:
        invoiceDetails.invoiceAdditionalDiscountAmount,
      invoiceAdditionalFeeAmount: invoiceDetails.invoiceAdditionalFeeAmount,

      supplierCode: invoiceDetails.supplierCode,
      supplierName: invoiceDetails.supplierName,
      supplierTin: invoiceDetails.supplierTin,
      supplierRegistrationType: invoiceDetails.supplierRegistrationType,
      supplierRegistrationNumber: invoiceDetails.supplierRegistrationNumber,
      supplierSstRegistrationNumber:
        invoiceDetails.supplierSstRegistrationNumber,
      supplierMsicCode: invoiceDetails.supplierMsicCode,
      supplierBusinessActivityDescription:
        invoiceDetails.supplierBusinessActivityDescription,
      supplierTourismTaxRegistrationNumber:
        invoiceDetails.supplierTourismTaxRegistrationNumber,
      supplierEmail: invoiceDetails.supplierEmail,
      supplierContact: invoiceDetails.supplierContact,
      supplierAddressLine0: invoiceDetails.supplierAddressLine0,
      supplierAddressLine1: invoiceDetails.supplierAddressLine1,
      supplierAddressLine2: invoiceDetails.supplierAddressLine2,
      supplierCity: invoiceDetails.supplierCity,
      supplierPostcode: invoiceDetails.supplierPostcode,
      supplierCountry: invoiceDetails.supplierCountry,
      supplierState: invoiceDetails.supplierState,

      buyerCode: invoiceDetails.buyerCode,
      buyerName: invoiceDetails.buyerName,
      buyerTin: invoiceDetails.buyerTin,
      buyerRegistrationType: invoiceDetails.buyerRegistrationType, // updated as per request
      buyerRegistrationNumber: invoiceDetails.buyerRegistrationNumber,
      buyerSstRegistrationNumber: invoiceDetails.buyerSstRegistrationNumber,
      buyerEmail: invoiceDetails.buyerEmail,
      buyerContact: invoiceDetails.buyerContact,
      buyerAddressLine0: invoiceDetails.buyerAddressLine0,
      buyerAddressLine1: invoiceDetails.buyerAddressLine1,
      buyerAddressLine2: invoiceDetails.buyerAddressLine2,
      buyerCity: invoiceDetails.buyerCity,
      buyerPostcode: invoiceDetails.buyerPostcode,
      buyerCountry: invoiceDetails.buyerCountry,
      buyerState: invoiceDetails.buyerState,

      lineItems: invoiceDetails.lineItems,
      jobCode: invoiceDetails.jobCode,
      docDate: invoiceDetails.docDate
        ? format(invoiceDetails.docDate, "yyyy-MM-dd")
        : null,

      //shipping
      shipping: invoiceDetails.shipping,

      //import and exports
      customs: invoiceDetails.customs,
    };

    setSupplierCode(invoiceDetails.supplierCode);
    setBuyerCode(invoiceDetails.buyerCode);

    if (!["01", "01A", "11"].includes(documentType)) {
      const referenceDetail = invoiceDetails.originalInvoiceReference
        .map((value) => value.uuid)
        .join(" ; ");
      setInvoiceReferenceSubstitution(referenceDetail);
    }

    return {
      zodSchema: getValidationSchema(documentType),
      defaultValues: invoiceDetailsData,
    };
  }, [documentType, user, invoiceDetails]);

  type FormData = z.infer<typeof zodSchema> & {
    originalInvoiceReference?: Record<string, string>[];
  };

  const cleanShippingAndCustoms = (data: FormData): FormData => {
    const isEmpty = (obj: any) =>
      !obj || Object.values(obj).every((v) => v == null || v === "");

    return {
      ...data,
      shipping: isEmpty(data.shipping) ? undefined : data.shipping,
      customs: isEmpty(data.customs) ? undefined : data.customs,
    };
  };

  const resolver = (values: any, context: any, options: any) =>
    zodResolver(zodSchema)(cleanShippingAndCustoms(values), context, options);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver,
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const calculateTotalTaxPerType = () => {
    const items = getValues("items") || [];

    const map = new Map<
      "01" | "02" | "03" | "04" | "05" | "06" | "E",
      number
    >();

    for (const item of items) {
      if (!item || item._delete) continue;

      const taxType = item.taxType;
      const taxAmount = Number(item.taxAmount ?? 0);

      if (!taxType) continue;

      map.set(taxType, (map.get(taxType) || 0) + taxAmount);
    }

    const grouped = Array.from(map.entries()).map(([taxType, taxAmount]) => ({
      taxType,
      taxAmount: roundTotal(taxAmount),
    }));

    return grouped;
  };

  const handleCalculation = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    const items = getValues("items"); // Get current form state
    const item = items[index];

    if (!item) return;
    const updatedItem = {
      ...item,
      [field]: Number(value) || 0, // Convert input to number
    };

    const toSafeNumber = (value: any): number => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    const quantity = toSafeNumber(updatedItem.quantity);
    const unitPrice = toSafeNumber(updatedItem.unitPrice);
    const discountRate = toSafeNumber(updatedItem.discountRate);
    const feeRate = toSafeNumber(updatedItem.feeRate);

    const subtotal = quantity * unitPrice;
    const discountAmount = subtotal * (discountRate / 100);

    // According to Mr Ng, we should follow customs
    // service charge are not included in taxable amount
    const taxableAmount = subtotal - discountAmount;

    // use taxable amount due to is the same for charge
    const feeChargeAmount = taxableAmount * (feeRate / 100);

    let taxAmount, taxRate;

    if (field === "taxAmount") {
      // If tax amount is manually entered and editable
      taxAmount = Number(value);
      taxRate = taxableAmount > 0 ? (taxAmount / taxableAmount) * 100 : 0;
    } else {
      // Normal calculation using tax rate
      taxRate = Number(updatedItem.taxRate ?? 0);
      taxAmount = taxableAmount * (taxRate / 100);
    }

    const totalExcludingTax = taxableAmount + feeChargeAmount;
    const total = totalExcludingTax + taxAmount; // also known as total after tax

    setValue(`items.${index}.subtotal`, subtotal, { shouldValidate: true });
    setValue(`items.${index}.discountAmount`, discountAmount, {
      shouldValidate: true,
    });
    setValue(`items.${index}.feeAmount`, feeChargeAmount, {
      shouldValidate: true,
    });
    setValue(`items.${index}.taxAmount`, taxAmount, { shouldValidate: true });
    setValue(`items.${index}.totalExcludingTax`, totalExcludingTax, {
      shouldValidate: true,
    });
    setValue(`items.${index}.total`, total);
    setCalcTrigger((c) => c + 1);
  };

  const DocumentTypeLabels: Record<string, string> = {
    "01": "Invoice",
    "02": "Credit Note",
    "03": "Debit Note",
    "04": "Refund Note",
    "11": "Self-Billed Invoice",
    "12": "Self-Billed Credit Note",
    "13": "Self-Billed Debit Note",
    "14": "Self-Billed Refund Note",
    "01A": "Consolidated Invoice",
  };

  /**
   * Printed heading, per document type. Without this every type printed the
   * generic "Invoice" heading — wrong for a credit note as much as for a
   * self-billed one. Mirrors DocumentTypeLabels except for 11, which LHDN
   * calls a Self-Billed E-Invoice.
   */
  const PrintHeadings: Record<string, string> = {
    ...DocumentTypeLabels,
    "11": "Self-Billed E-Invoice",
  };

  useEffect(() => {
    const loadInvoiceDetails = async () => {
      if (!invoiceDetails) return;

      isLoadingRef.current = true;
      setIsInvoiceLoading(true);
      reset(defaultValues);

      // Batch append all line items in a single call to avoid 1 re-render per item
      const allLineItems = (invoiceDetails.lineItems || []).map((item) => {
        const total =
          round(item.totalExcludingTax + item.taxAmount) || Number(0);

        return {
          id: item.id,
          classification: item.classification,
          description: item.description || "",
          unitPrice: item.unitPrice || 0,
          taxType: item.taxType,
          taxRate: item.taxRate || 0,
          taxAmount: item.taxAmount,
          subtotal: item.subtotal,
          totalExcludingTax: item.totalExcludingTax,
          quantity: item.quantity || 1,
          discountRate: item.discountRate || 0,
          discountAmount: item.discountAmount || 0,
          feeRate: item.feeChargeRate || 0,
          feeAmount: item.feeChargeAmount || 0,
          codeNumber: item.itemCode,
          detailsOfTaxExemption: item.detailsOfTaxExemption,
          amountExemptedFromTax: item.amountExemptedFromTax ?? null,
          productTariffCode: item.productTariffCode,
          countryOfOrigin: item.countryOfOrigin,
          measurement: item.measurement ?? null,
          total: total,
        };
      });
      if (allLineItems.length > 0) {
        append(allLineItems);
      }

      if (invoiceDetails) {
        setValue("documentType", invoiceDetails.typeCode as DocumentType);

        // Explicitly set supplier fields that may not be registered in the form
        // (supplier section is not rendered for types 01, 01A, 02, 03, 04)
        // Without this, unregistered fields may lose their values after reset()
        setValue(
          "supplierBusinessActivityDescription",
          invoiceDetails.supplierBusinessActivityDescription || "",
        );
        setValue("supplierMsicCode", invoiceDetails.supplierMsicCode || "");
        setValue("supplierName", invoiceDetails.supplierName || "");
        setValue("supplierTin", invoiceDetails.supplierTin || "");
        setValue(
          "supplierRegistrationType",
          invoiceDetails.supplierRegistrationType || "",
        );
        setValue(
          "supplierRegistrationNumber",
          invoiceDetails.supplierRegistrationNumber || "",
        );
        setValue("supplierEmail", invoiceDetails.supplierEmail || "");
        setValue("supplierContact", invoiceDetails.supplierContact || "");
        setValue(
          "supplierAddressLine0",
          invoiceDetails.supplierAddressLine0 || "",
        );
        setValue("supplierCity", invoiceDetails.supplierCity || "");
        setValue("supplierCountry", invoiceDetails.supplierCountry || "");
        setValue("supplierState", invoiceDetails.supplierState || "");

        if (
          invoiceDetails.originalInvoiceReference &&
          Array.isArray(invoiceDetails.originalInvoiceReference)
        ) {
          const referenceUuids = invoiceDetails.originalInvoiceReference
            .map((ref: any) => ref.uuid)
            .join(" ; ");
          setInvoiceReferenceSubstitution(referenceUuids);
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      // Use stored header values — do not recalculate from line items on load.
      // Line item tax amounts from imports/API/consolidation may have minor rounding
      // differences vs the stored total, which is always the authoritative figure.
      setTotals({
        totalExcludingTax: Number(invoiceDetails.totalExcludingTax),
        totalIncludingTax: Number(invoiceDetails.totalIncludingTax),
        totalTaxAmount: Number(invoiceDetails.totalTaxAmount),
        totalPayableAmount: Number(invoiceDetails.totalPayableAmount),
      });
      isLoadingRef.current = false;
      setIsInvoiceLoading(false);
    };

    loadInvoiceDetails();
  }, [defaultValues, invoiceDetails]);

  const [items = []] = watch(["items"]);
  const [buyerTin, supplierTin] = watch(["buyerTin", "supplierTin"]);
  const invoiceAdditionalDiscountAmount =
    watch("invoiceAdditionalDiscountAmount") ?? 0;
  const invoiceAdditionalFeeAmount = watch("invoiceAdditionalFeeAmount") ?? 0;
  const roundingAmount = watch("roundingAmount") ?? 0;
  const currencyCode = watch("currencyCode");

  const [totals, setTotals] = useState({
    totalExcludingTax: 0,
    totalIncludingTax: 0,
    totalTaxAmount: 0,
    totalPayableAmount: 0,
  });
  // True while loadInvoiceDetails is running — prevents the recalculation useEffect
  // from overwriting stored header values with a recalculated sum from line items.
  // Initialized as false so new invoice creation (no invoiceDetails) recalculates normally.
  const isLoadingRef = useRef(false);
  // Incremented by handleCalculation so the recalculation useEffect reliably fires
  // on user edits. Needed because setValue on nested item paths does not change the
  // items array reference, so items alone cannot trigger the useEffect.
  const [calcTrigger, setCalcTrigger] = useState(0);

  useEffect(() => {
    // Skip recalculation while the invoice is being loaded from the database.
    // Totals are set directly from stored header values after load completes.
    // Recalculation only runs when the user actively edits line items.
    if (isLoadingRef.current) return;

    const totals = items.reduce(
      (acc, item) => {
        if (item._delete) {
          return acc;
        }

        const itemTotal = Number(item.total || 0);
        const itemTotalExcludingTax = Number(item.totalExcludingTax || 0);
        const itemTaxAmount = Number(item.taxAmount || 0);

        return {
          totalExcludingTax: acc.totalExcludingTax + itemTotalExcludingTax,
          totalIncludingTax: acc.totalIncludingTax + itemTotal,
          totalTaxAmount: acc.totalTaxAmount + itemTaxAmount,
          totalPayableAmount: acc.totalPayableAmount + itemTotal,
        };
      },
      {
        totalExcludingTax: 0,
        totalIncludingTax: 0,
        totalTaxAmount: 0,
        totalPayableAmount: 0,
      },
    );

    setTotals(
      deriveDocumentTotals({
        rawExcludingTax: totals.totalExcludingTax,
        totalTaxAmount: calculateTotalTaxPerType().reduce(
          (sum, entry) => sum + entry.taxAmount,
          0,
        ),
        additionalDiscountAmount: invoiceAdditionalDiscountAmount,
        additionalFeeAmount: invoiceAdditionalFeeAmount,
        roundingAmount,
      }),
    );
  }, [
    items,
    calcTrigger,
    invoiceAdditionalDiscountAmount,
    invoiceAdditionalFeeAmount,
    roundingAmount,
  ]);

  if (!user || !zodSchema || !defaultValues || isInvoiceLoading) {
    return <LoadingIndicator />;
  }

  const formatCurrency = (amount: number): string => {
    const symbol =
      currencyCodes.find((c) => c.Code === currencyCode)?.Symbol ??
      currencyCode ??
      "RM";
    const formatted = new Intl.NumberFormat("en-MY", {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(amount);
    return `${symbol} ${formatted}`;
  };

  const getBase64FromUrl = async (url: string): Promise<string> => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  const roundToTwoDecimals = (
    value: number | string | null | undefined,
  ): number => {
    const num = Number(value);
    if (isNaN(num)) return 0;
    return round(num);
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    const formData = getValues();
    const myHeaders = new Headers();
    myHeaders.append("x-api-key", "2c9c67d3-948d-4ef5-88e4-93871d96e4f6");
    myHeaders.append("Content-Type", "application/json");
    const LHDN_QR_BASE_URL = import.meta.env.VITE_LDHN_BASE_URL;

    const base64 = await getBase64FromUrl(
      `${import.meta.env.VITE_BACKEND_API_BASE_URL}/company-logo/${
        user.companyLogo
      }`,
    );

    const isHQ =
      !currentActiveBranch || currentActiveBranch.prefixCode === "HQ";
    const isSelfBilled = ["11", "12", "13", "14"].includes(documentType);
    const raw = JSON.stringify({
      supplier: {
        logo: user.companyLogo ? base64 : false,
        name: isSelfBilled ? formData.supplierName : user.businessName,
        branchName:
          !isSelfBilled && !isHQ ? currentActiveBranch.branchName : undefined,
        address: (() => {
          if (isSelfBilled) {
            return [
              formData.supplierAddressLine0,
              formData.supplierAddressLine1,
              formData.supplierAddressLine2,
              formData.supplierPostcode,
              states.find((state) => state.Code == formData.supplierState)
                ?.State,
              countries.find(
                (country) => country.Code == formData.supplierCountry,
              )?.Country,
            ]
              .filter(Boolean)
              .join(", ");
          }
          const companyAddress = [
            user.addressLine0,
            user.addressLine1,
            user.addressLine2,
            user.postalZone,
            user.cityName,
          ]
            .filter(Boolean)
            .join(", ");
          if (isHQ) return companyAddress;
          const branchAddress = [
            currentActiveBranch.addressLine0,
            currentActiveBranch.addressLine1,
            currentActiveBranch.addressLine2,
            currentActiveBranch.postalZone,
            currentActiveBranch.cityName,
          ]
            .filter(Boolean)
            .join(", ");
          return branchAddress || companyAddress;
        })(),
        // The print service renders these unconditionally (contactEmail goes
        // through textWithLink), so they must never be null.
        contactEmail: isSelfBilled
          ? formData.supplierEmail || "NA"
          : ((isHQ ? user.email : currentActiveBranch.email) ?? user.email),
        contactPhone: isSelfBilled
          ? formData.supplierContact || "NA"
          : ((isHQ ? user.contactNumber : currentActiveBranch.contactNumber) ??
            user.contactNumber),
        TIN: formData.supplierTin,
        registrationNumber: formData.supplierRegistrationNumber,
        SSTID: formData.supplierSstRegistrationNumber || "NA",
        MSICCode: formData.supplierMsicCode,
        businessActivity: formData.supplierBusinessActivityDescription || "NA",
      },
      invoice: {
        title: "Invoice",
        heading: PrintHeadings[documentType],
        type: `${documentType} ${DocumentTypeLabels[documentType]}`,
        version: "1.0",
        code: formData.codeNumber,
        uniqueIdentifier:
          eInvoiceDetails.data && eInvoiceDetails?.data[0].uuid
            ? eInvoiceDetails.data[0].uuid
            : "NA",
        originalInvoiceRef: "Not Applicable",
        posReferenceNumber: formData.originalInvoiceNumber ?? "NA",

        dateLabel: "e-Invoice Date and Time",
        dateTime: formData.invoiceDate.replace("T", " "),

        docDate:
          formData.docDate &&
          formData.docDate.slice(0, 10) !== formData.invoiceDate.slice(0, 10)
            ? formData.docDate.slice(0, 10)
            : undefined,
      },
      buyer: {
        TIN: getValues("buyerTin"),
        name: formData.buyerName,
        identificationNumber: getValues("buyerRegistrationNumber"),
        address: [
          formData.buyerAddressLine0,
          formData.buyerAddressLine1,
          formData.buyerAddressLine2,
          formData.buyerPostcode,
          states.find((state) => state.Code == formData.buyerState)?.State,
          countries.find((country) => country.Code == formData.buyerCountry)
            ?.Country,
        ]
          .filter(Boolean) // Remove empty/null/undefined values
          .join(", "), // Join with spaces
        contactNumber: formData.buyerContact,
        email: formData.buyerEmail,
      },
      items: formData.items.map((item) => {
        // Convert to numbers first
        const quantity = Number(item.quantity);
        const unitPrice = Number(item.unitPrice);
        const discountRate = Number(item.discountRate);
        const taxRate = Number(item.taxRate);
        const feeChargeRate = Number(item.feeRate);

        // Calculate amounts
        const amount = quantity * unitPrice;
        const discount = amount * (discountRate / 100);
        const afterDiscount = amount - discount;

        const taxAmount = Number(item.taxAmount);
        const feeChargeAmount =
          feeChargeRate > 0
            ? afterDiscount * (feeChargeRate / 100)
            : Number(item.feeAmount || 0);

        return {
          classification: item.classification,
          description: item.description,
          quantity: quantity,
          unitPrice: formatCurrency(unitPrice),
          amount: formatCurrency(amount),
          discount: discountRate + "%",
          taxRate: taxRate + "%",
          taxAmount: formatCurrency(taxAmount),
          // code: item.codeNumber,
          totalPrice: formatCurrency(
            afterDiscount + taxAmount + feeChargeAmount,
          ),
        };
      }),
      // Calculate tourism tax separately
      tourismTax: (() => {
        const tourismTaxItems = formData.items.filter(
          (item) => item.taxType === "03",
        );
        if (tourismTaxItems.length === 0) return null;

        const tourismTaxTotal = tourismTaxItems.reduce((sum, item) => {
          const quantity = Number(item.quantity);
          const unitPrice = Number(item.unitPrice);
          const discountRate = Number(item.discountRate);
          const amount = quantity * unitPrice;
          const discount = amount * (discountRate / 100);
          return sum + (amount - discount);
        }, 0);

        const tourismTaxAmount = tourismTaxItems.reduce(
          (sum, item) => sum + Number(item.taxAmount),
          0,
        );

        // Get the tax rate from the first tourism tax item
        const tourismTaxRate = Number(tourismTaxItems[0]?.taxRate ?? 0);

        return {
          taxableAmount: formatCurrency(tourismTaxTotal),
          taxType: "Tourism Tax",
          taxRate: tourismTaxRate + "%",
          taxAmount: formatCurrency(tourismTaxAmount),
        };
      })(),
      totals: (() => {
        // Calculate tax breakdown by tax type
        // Tax types: 01 = Sales Tax, 02 = Service Tax, 03 = Tourism Tax, 04 = High-Value Goods Tax,
        //            05 = Sales Tax on Low Value Goods, 06 = Not Applicable, E = Tax Exemption
        const taxBreakdown: Record<string, string> = {};
        const taxRates: Record<string, string> = {};
        const taxableAmounts: Record<string, string> = {};
        // Compute total tax from items to ensure all tax types (incl. tourism) are included
        const totalTaxFromItems = formData.items.reduce(
          (sum, item) => sum + Number(item.taxAmount),
          0,
        );

        // Group tax amounts, rates, and taxable amounts by tax type
        const taxByType = formData.items.reduce(
          (acc, item) => {
            const taxType = item.taxType;
            const taxAmount = Number(item.taxAmount);
            const taxRate = Number(item.taxRate);
            // Use stored totalExcludingTax as the taxable base
            const taxableAmount = Number(item.totalExcludingTax);

            if (taxType) {
              if (!acc[taxType]) {
                acc[taxType] = {
                  taxAmount: 0,
                  taxableAmount: 0,
                  taxRate: taxRate,
                };
              }
              acc[taxType].taxAmount += taxAmount;
              acc[taxType].taxableAmount += taxableAmount;
              // Keep the first non-zero tax rate found for this type
              if (taxRate > 0 && acc[taxType].taxRate === 0) {
                acc[taxType].taxRate = taxRate;
              }
            }
            return acc;
          },
          {} as Record<
            string,
            { taxAmount: number; taxableAmount: number; taxRate: number }
          >,
        );

        // Convert to formatted currency strings
        for (const [taxType, data] of Object.entries(taxByType)) {
          if (data.taxAmount !== 0) {
            taxBreakdown[taxType] = formatCurrency(data.taxAmount);
            taxableAmounts[taxType] = formatCurrency(data.taxableAmount);
            // Tourism tax (03) is amount-based, not rate-based — show "-"
            if (taxType === "03") {
              taxRates[taxType] = "-";
            } else {
              taxRates[taxType] =
                `${roundTo(data.taxRate, RATE_DISPLAY_PRECISION).toFixed(RATE_DISPLAY_PRECISION)}%`;
            }
          }
        }

        return {
          subtotal: formatCurrency(Number(totals?.totalExcludingTax ?? 0)),
          totalExcludingTax: formatCurrency(
            Number(totals?.totalExcludingTax ?? 0),
          ),
          taxAmount: formatCurrency(totalTaxFromItems),
          taxBreakdown:
            Object.keys(taxBreakdown).length > 0 ? taxBreakdown : undefined,
          taxRates: Object.keys(taxRates).length > 0 ? taxRates : undefined,
          taxableAmounts:
            Object.keys(taxableAmounts).length > 0 ? taxableAmounts : undefined,
          totalIncludingTax: formatCurrency(
            Number(totals?.totalIncludingTax ?? 0),
          ),
          totalPayable: formatCurrency(Number(totals?.totalPayableAmount ?? 0)),
        };
      })(),
      digitalSignature:
        "9e83e05bbf95db17ac0deec3b7ce6cba983f6dc50531c7a919f28d5fb369etc3",
      termsAndConditions: `This documentation is a digital copy of the original document and no signature is required. ${user.reportFooter}`,
      ...(eInvoiceDetails.data != null && eInvoiceDetails.data[0].longId
        ? {
            validationDateTime: format(
              new Date(eInvoiceDetails.data[0].dateTimeValidated),
              "yyyy-MM-dd'T'HH:mm:ss",
            )
              .slice(0, 19)
              .replace("T", " "),
            qrCodeUrl: `${LHDN_QR_BASE_URL}/${eInvoiceDetails.data[0].uuid}/share/${eInvoiceDetails.data[0].longId}`,
          }
        : {
            validationDateTime: "NA",
            qrCodeUrl: false,
          }),
    });

    const requestOptions: RequestInit = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };
    fetch("https://print.quickbill.my/pdf/supplier123", requestOptions)
      .then(async (response) => {
        if (!response.ok) {
          const errorBody = await response.json().catch(() => null);
          const message =
            errorBody?.error ||
            errorBody?.message ||
            `HTTP error! status: ${response.status}`;
          throw new Error(message);
        }
        return response.blob();
      })
      .then((blob) => {
        const urlBlob = URL.createObjectURL(blob);
        window.open(urlBlob, "_blank");
      })
      .catch((error) => {
        console.error("Error:", error);
        alert(`Failed to generate PDF: ${error.message}`);
      })
      .finally(() => setIsPrinting(false));
  };

  /**
   * Refreshes everything that renders an invoice status
   */
  const refreshInvoiceStatus = () => {
    queryClient.invalidateQueries({ queryKey: ["invoices"] });
    queryClient.invalidateQueries({
      queryKey: ["eInvoiceDetails", invoiceDetails?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ["invoicesDetails", invoiceDetails?.id],
    });
  };

  /**
   * Cancels the invoice via the backend
   */
  const handleCancel = async () => {
    if (!invoiceDetails?.id) return;

    setIsCancelling(true);
    try {
      const result = await cancelEInvoice({
        invoiceId: invoiceDetails.id,
        reason: cancelReason,
      });

      // Cancelled, or already cancelled in MyTax and now synchronized
      if (result.success) {
        refreshInvoiceStatus();
        setCancelReason("");

        toast({
          title:
            result.outcome === "already-cancelled"
              ? "Status Synchronized"
              : "Invoice Cancelled",
          description:
            result.consolidatedRevert?.reverted === false
              ? `${result.message} ${result.consolidatedRevert.message}`
              : result.message,
          icon: (
            <CheckCircle
              size={30}
              className="animate-spin-once text-green-600"
            />
          ),
          duration: 8000,
        });
        return;
      }

      // Not cancellable
      if (result.synchronized) {
        refreshInvoiceStatus();
      }

      toast({
        title:
          result.outcome === "not-cancellable"
            ? "Cannot Cancel Invoice"
            : "Cancellation Failed",
        variant: "destructive",
        description: result.message,
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 8000,
      });
    } catch (error) {
      console.error("Cancellation failed:", error);
      toast({
        title: "Cancellation Failed",
        variant: "destructive",
        description: "Failed to cancel invoice. Please try again.",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteConsolidatedInvoice = async () => {
    if (!invoiceDetails) return;

    try {
      await deleteInvoiceAsync(invoiceDetails.id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["invoices"] });
          toast({
            title: "Consolidated Invoice Deleted",
            description:
              "Consolidated invoice has been deleted and individual invoices reverted to draft status.",
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-600"
              />
            ),
            duration: 5000,
          });
          navigate("/invoices");
        },
        onError: (error: any) => {
          toast({
            title: "Deletion Failed",
            variant: "destructive",
            description:
              error?.response?.data?.message ||
              error?.message ||
              "Failed to delete consolidated invoice. Please try again.",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: 5000,
          });
        },
      });
    } catch (error) {
      console.error("Error deleting consolidated invoice:", error);
    }
  };

  const onSubmit = async (data: z.infer<typeof zodSchema>) => {
    if (!invoiceDetails) return;

    const {
      items,
      invoiceDate,
      invoiceTime,
      documentType,
      billingPeriodStartDate,
      billingPeriodEndDate,
      prepaymentDate,
      docDate,
      ...formData
    } = data;

    const remainingItems = items.filter((item) => !item._delete);

    if (remainingItems.length <= 0) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: "Items cannot be empty",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 4000,
      });

      return;
    }

    // Removed validation: "one UUID one line item" restriction
    // Credit Notes (02, 03, 04) and Self-Billed Credit Notes (12, 13, 14)
    // can now have multiple line items per UUID to match the original invoice structure

    const dataToBeSave = {
      id: invoiceDetails.id,
      invoice: {
        ...formData,

        buyerCode: buyerCode,
        supplierCode: supplierCode,
        typeCode: documentType,

        // LegalMonetaryTotal
        totalExcludingTax: roundTotal(totals.totalExcludingTax),
        totalIncludingTax: roundTotal(totals.totalIncludingTax),
        totalPayableAmount: roundTotal(totals.totalPayableAmount),
        totalNetAmount: null, // Default 0
        totalDiscountValue: null, // Default 0
        totalFeeChargeAmount: null, // Default 0

        // taxTotal
        totalTaxAmount: roundTotal(totals.totalTaxAmount),
        roundingAmount: roundingAmount, // Default 0
        totalTaxableAmountPerTaxType: null, // Default 0
        // totalTaxAmountPerTaxType: [{ taxAmount: 0, taxType: "06" as const }], // Default 0
        totalTaxAmountPerTaxType: calculateTotalTaxPerType(),
        taxType: "06" as const, // Default to TaxCode.NotApplicable

        invoiceDate: new Date(invoiceDate),
        invoiceTime: new Date(invoiceDate),
        docDate: docDate === null ? docDate : new Date(docDate),
        billingPeriodStartDate:
          billingPeriodStartDate === null
            ? billingPeriodStartDate
            : new Date(billingPeriodStartDate),
        billingPeriodEndDate:
          billingPeriodEndDate === null
            ? billingPeriodEndDate
            : new Date(billingPeriodEndDate),
        prepaymentDate:
          prepaymentDate === null ? prepaymentDate : new Date(prepaymentDate),
        lineItems: items.map((item) => {
          const { codeNumber, ...itemDetails } = item;
          return {
            ...itemDetails,
            itemCode: codeNumber,
            unitPrice: roundToTwoDecimals(itemDetails.unitPrice),
            subtotal: roundToTwoDecimals(itemDetails.subtotal),
            discountAmount: roundToTwoDecimals(itemDetails.discountAmount),
            feeAmount: roundToTwoDecimals(itemDetails.feeAmount),
            taxAmount: roundToTwoDecimals(itemDetails.taxAmount),
            totalExcludingTax: roundToTwoDecimals(
              itemDetails.totalExcludingTax,
            ),
            total: roundToTwoDecimals(itemDetails.total),
          };
        }),
      },
    };
    await updateInvoice(dataToBeSave, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["invoices"] });
        toast({
          title: "Invoice Saved",
          description: "Invoice successfully saved.",
          icon: (
            <CheckCircle
              size={30}
              className="animate-spin-once text-green-600"
            />
          ),
          duration: 5000,
        });
        navigate("/invoices");
      },
      onError: (error: any) => {
        toast({
          title: "Failed",
          variant: "destructive",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to save invoice. Please try again.",
          icon: <CircleAlert size={30} className="animate-spin-once" />,
          duration: 5000,
        });
      },
    });
  };

  const onError = (errors: FieldErrors<FormData>) => {
    console.error(errors);
    let description = "Please refresh and try again.";
    description = Object.values(errors)[0]?.message as string;
    toast({
      title: "Action failed",
      variant: "destructive",
      description: description,
      icon: <CircleAlert size={30} className="animate-spin-once" />,
      duration: 4000,
    });
  };

  const handleSelectItem = (item: z.infer<typeof invoiceItemSchema>) => {
    const { id, codeNumber, ...otherItemDetails } = item;

    const modifiedItem = {
      ...otherItemDetails,
      id: undefined,
      codeNumber: id ?? codeNumber,
    };
    append(modifiedItem);
    handleCalculation(fields.length, "quantity", 1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Submitted":
        return "blue";
      case "Valid":
        return "green";
      case "Invalid":
        return `red`;
      case "Consolidated":
        return "brown";
      default:
        return `gray`;
    }
  };

  const handleSelectEInvoice = async (EInvoices: EInvoice[]) => {
    const reference = EInvoices.map((value) => ({
      id: value.internalId,
      uuid: value.id,
      invoiceId: value.invoiceId,
    }));
    const referenceNumber = EInvoices.map((value) => value.id).join(" ; ");
    setInvoiceReferenceSubstitution(referenceNumber);
    setValue("originalInvoiceReference", reference);

    try {
      const invoiceIds = EInvoices.map((e) => e.invoiceId);
      const response = await api.post("/e-invoices/by-ids", { invoiceIds });
      const invoicesWithLineItems = response.data;

      // Copy all line items from all selected invoices
      const allLineItems: z.infer<typeof invoiceItemSchema>[] = [];

      for (const invoice of invoicesWithLineItems) {
        if (invoice.lineItems && Array.isArray(invoice.lineItems)) {
          for (const lineItem of invoice.lineItems) {
            const totalExcludingTax = Number(lineItem.totalExcludingTax) || 0;
            const taxAmount = Number(lineItem.taxAmount) || 0;
            const total = totalExcludingTax + taxAmount;

            allLineItems.push({
              classification: lineItem.classification,
              description: lineItem.description,
              unitPrice: Number(lineItem.unitPrice),
              taxType: lineItem.taxType,
              taxRate: Number(lineItem.taxRate),
              taxAmount: taxAmount,
              detailsOfTaxExemption: lineItem.detailsOfTaxExemption,
              amountExemptedFromTax: lineItem.amountExemptedFromTax,
              subtotal: Number(lineItem.subtotal),
              totalExcludingTax: totalExcludingTax,
              quantity: Number(lineItem.quantity),
              measurement: lineItem.measurement,
              discountRate: Number(lineItem.discountRate),
              discountAmount: Number(lineItem.discountAmount),
              feeRate: Number(lineItem.feeChargeRate ?? 0),
              feeAmount: Number(lineItem.feeChargeAmount ?? 0),
              productTariffCode: lineItem.productTariffCode,
              codeNumber: lineItem.itemCode,
              countryOfOrigin: lineItem.countryOfOrigin,
              total: total,
              sourceInvoiceId: invoice.id,
            } as any);
          }
        }
      }

      setValue("items", allLineItems);
    } catch (error: any) {
      console.error("Error fetching invoice details:", error);
      toast({
        title: "Error",
        variant: "destructive",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch invoice details. Please try again.",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 4000,
      });
    }
  };

  //manual add line item
  const handleAddLineItem = () => {
    append(newLineItem);
    handleCalculation(items.length, "quantity", newLineItem.quantity);
    setNewLineItem(lineItemDefaultValue);
  };

  const originalInvoiceReferenceData =
    getValues("originalInvoiceReference") || [];

  let defaultEInvoiceChecked: string[] = [];

  if (originalInvoiceReferenceData.length > 0) {
    const invoiceIds = originalInvoiceReferenceData
      .map((ref: any) => ref.invoiceId)
      .filter((id: any) => id !== undefined);

    if (invoiceIds.length > 0) {
      defaultEInvoiceChecked = invoiceIds;
    } else {
      defaultEInvoiceChecked = originalInvoiceReferenceData
        .map((ref: any) => ref.id)
        .filter((id: any) => id !== undefined);
    }
  }

  if (defaultEInvoiceChecked.length === 0) {
    defaultEInvoiceChecked = items
      .map((item) => item.codeNumber)
      .filter((code) => code !== undefined);
  }

  const handleSelectBuyer = (buyer: z.infer<typeof buyerSchema>) => {
    setBuyerCode(buyer.code);
    setValue("buyerRegistrationType", buyer.registrationType);
    setValue("buyerRegistrationNumber", buyer.registrationNumber);
    setValue("buyerTin", buyer.tin);
    setValue("buyerName", buyer.name);
    setValue("buyerSstRegistrationNumber", buyer.sstRegistrationNumber ?? "");
    setValue("buyerEmail", buyer.email);
    setValue("buyerContact", buyer.contactNumber);
    setValue("buyerAddressLine0", buyer.buyerAddress.addressLine0);
    setValue("buyerAddressLine1", buyer.buyerAddress.addressLine1);
    setValue("buyerAddressLine2", buyer.buyerAddress.addressLine2);
    setValue("buyerCity", buyer.buyerAddress.cityName);
    setValue("buyerPostcode", buyer.buyerAddress.postalZone);
    setValue("buyerState", buyer.buyerAddress.state);
    setValue("buyerCountry", buyer.buyerAddress.country);
  };

  const handleSelectSupplier = (supplier: z.infer<typeof supplierSchema>) => {
    setValue("supplierRegistrationType", supplier.registrationType);
    setValue("supplierRegistrationNumber", supplier.registrationNumber);
    setValue("supplierTin", supplier.tin);
    setValue("supplierName", supplier.name);
    setValue("supplierSstRegistrationNumber", supplier.sstRegistrationNumber);
    setValue("supplierMsicCode", supplier.msicCode);
    setValue(
      "supplierTourismTaxRegistrationNumber",
      supplier.tourismTaxRegistrationNumber,
    );
    setValue(
      "supplierBusinessActivityDescription",
      supplier.businessActivityDescription ?? "",
    );
    setValue("supplierEmail", supplier.email);
    setValue("supplierContact", supplier.contactNumber);
    setValue("supplierAddressLine0", supplier.address.addressLine0);
    setValue("supplierAddressLine1", supplier.address.addressLine1);
    setValue("supplierAddressLine2", supplier.address.addressLine2);
    setValue("supplierCity", supplier.address.cityName);
    setValue("supplierPostcode", supplier.address.postalZone);
    setValue("supplierState", supplier.address.state);
    setValue("supplierCountry", supplier.address.country);
  };

  if (isLoading || eInvoiceDetailsIsLoading) {
    return <LoadingIndicator />;
  }

  if (!invoiceDetails || error) {
    return <InvoiceNotFound />;
  }

  // put under loading return to avoid eInvoiceDetails fetching is not completed yet
  const isReadOnly =
    ["Valid", "Cancelled", "Submitted"].includes(
      eInvoiceDetails?.data?.[0]?.status,
    ) ||
    (invoiceDetails.isConsolidated && documentType !== "01A");

  // this constant function is put under loading to prevent invoice did not have data
  // if eInvoice Details is loading
  // to abstract the error message return from LHDN
  const eInvoiceDetail = eInvoiceDetails.data?.[0];

  const renderValidationErrors = () => {
    return eInvoiceDetail?.validationResults?.validationSteps
      ?.filter((step: any) => step.status === "Invalid")
      .map((error: any) => (
        <>
          <Callout.Icon>
            <TriangleAlert />
          </Callout.Icon>
          <Callout.Text>
            {error.error?.innerError?.[0]?.error || "Unknown validation error"}
          </Callout.Text>
        </>
      ));
  };

  const renderDetailErrors = () => {
    return eInvoiceDetail?.errorContent?.details?.map((error: any) => (
      <>
        <Callout.Icon>
          <TriangleAlert />
        </Callout.Icon>
        <Callout.Text>
          {error.target} : {error.code} - {error.message}
        </Callout.Text>
      </>
    ));
  };

  const renderSingleError = () => {
    let errorMessage = null;

    // Scenario 1: payload error message
    if (typeof eInvoiceDetail?.errorContent?.error == "string") {
      errorMessage = eInvoiceDetail?.errorContent?.error;
    }
    // Scenario 2: The authenticated TIN and documents TIN is not matching
    else {
      errorMessage = `${eInvoiceDetail?.errorContent?.error?.details[0]?.code} - ${eInvoiceDetail?.errorContent?.error?.details[0]?.message}`;
    }
    return (
      <>
        <Callout.Icon>
          <TriangleAlert />
        </Callout.Icon>
        <Callout.Text>
          {errorMessage || "Oops! Error occurred when obtaining error details."}
        </Callout.Text>
      </>
    );
  };

  const handleRemoveItem = (index: number, typeCode: DocumentType) => {
    // remove saved line items
    if (items[index].id) {
      handleCalculation(index, "quantity", 0);
      setValue(`items.${index}._delete`, true);
      if (typeCode === "01A") {
        const data = {
          invoiceId: invoiceId,
          lineItemId: items[index].id,
        };

        deleteConsolidateItem(data, {
          onError: (error: any) => {
            toast({
              title: "Failed",
              variant: "destructive",
              description:
                error?.response?.data?.message ||
                error?.message ||
                "Failed to delete item. Please try again.",
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 3000,
            });
          },
        });
      } else {
        //original invoice reference cases
        if (!["01", "11", "01A"].includes(documentType)) {
          const filteredReference = invoiceDetails.originalInvoiceReference
            .filter((value) => !items[index].description.includes(value.id))
            .map((value) => value.uuid)
            .join(" ; ");
          setInvoiceReferenceSubstitution(filteredReference);
          const filteredInvoiceReference =
            invoiceDetails.originalInvoiceReference
              .filter((value) => !items[index].description.includes(value.id))
              .map((value) => value);
          setValue("originalInvoiceReference", filteredInvoiceReference);
        }
      }
    } else {
      //remove newly added line items
      remove(index);
    }
  };

  //manual insert original invoice reference uuid
  const handleInvoiceReference = (value: string) => {
    setInvoiceReferenceSubstitution((prev) => {
      const newValue = prev ? prev + " ; " + value : value;
      const uuids = newValue.split(" ; ");

      const references = uuids.map((value, index) => ({
        id: "ManualInsert" + index,
        uuid: value,
      }));
      setValue("originalInvoiceReference", references);
      return newValue;
    });
  };

  const handleConsolidateInvoice = (invoices: InvoiceList[]) => {
    const invoicesWithItemSchema: z.infer<typeof invoiceItemSchema>[] =
      invoices.flatMap((invoice) => {
        const raw = invoice.totalTaxAmountPerTaxType;
        const taxEntries: ConsolidateTaxEntry[] = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
            ? (() => {
                try {
                  return JSON.parse(raw);
                } catch {
                  return [];
                }
              })()
            : [];
        // Fallback: if no per-type breakdown, treat entire tax amount as Sales Tax
        const effectiveTaxEntries =
          taxEntries.length > 0
            ? taxEntries
            : [{ taxType: "01", taxAmount: Number(invoice.totalTaxAmount) }];

        // Use the sum of per-type tax entries
        const perTypeTaxTotal = effectiveTaxEntries.reduce(
          (sum, e) => sum + e.taxAmount,
          0,
        );
        const netTotalExcludingTax =
          Number(invoice.totalPayableAmount) - perTypeTaxTotal;

        // Give each tax type the share of the net that its own line items
        // actually account for, instead of loading it all onto the first entry.
        const netByEntry = allocateNetByTaxType(
          effectiveTaxEntries,
          netTotalExcludingTax,
        );

        return effectiveTaxEntries.map((taxEntry, index) => ({
          classification: "004",
          description: invoice.codeNumber,
          unitPrice: netByEntry[index],
          taxType: taxEntry.taxType as
            | "01"
            | "02"
            | "03"
            | "04"
            | "05"
            | "06"
            | "E",
          codeNumber: invoice.codeNumber,
          taxRate: 0,
          taxAmount: taxEntry.taxAmount,
          detailsOfTaxExemption: null,
          amountExemptedFromTax: null,
          subtotal: netByEntry[index],
          totalExcludingTax: netByEntry[index],
          quantity: 1,
          measurement: null,
          discountRate: null,
          discountAmount: null,
          feeRate: null,
          feeAmount: null,
          productTariffCode: null,
          total: round2(netByEntry[index] + taxEntry.taxAmount),
          countryOfOrigin: getValues("supplierCountry"),
        }));
      });
    append(invoicesWithItemSchema);
  };

  return (
    <Card className="w-full max-w-6xl mx-auto shadow p-2 pt-6">
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-6 md:gap-0">
            <div className="static md:absolute flex flex-row">
              <h1 className="text-xl font-bold text-center md:text-left me-2">
                {DocumentTypeLabels[documentType] || "Unknown Type"}
              </h1>
              {(eInvoiceDetails.data !== null ||
                invoiceDetails.isConsolidated) && (
                <Badge
                  color={getStatusColor(
                    invoiceDetails.isConsolidated
                      ? "Consolidated"
                      : eInvoiceDetails.data[0].status,
                  )}
                  size="3"
                  className="border border-3"
                >
                  {invoiceDetails.isConsolidated
                    ? "Consolidated"
                    : getInvoiceStatusLabel(eInvoiceDetails.data[0].status)}
                </Badge>
              )}
              {invoiceDetails.createdVia &&
                invoiceDetails.createdVia !== "manual" && (
                  <Badge
                    color={
                      invoiceDetails.createdVia === "api" ? "blue" : "green"
                    }
                    size="2"
                    variant="soft"
                    className="ml-1"
                  >
                    {invoiceDetails.createdVia === "api"
                      ? "API"
                      : "Excel Import"}
                  </Badge>
                )}
            </div>
            {/* Company details */}
            <div className="flex flex-col items-center justify-center w-full text-center space-y-3">
              <h1 className="text-2xl font-bold text-center md:text-left">
                {user.businessName}
              </h1>
              {currentActiveBranch &&
                currentActiveBranch.prefixCode !== "HQ" && (
                  <p className="text-base font-medium text-gray-700">
                    {currentActiveBranch.branchName}
                  </p>
                )}

              <div className="flex items-center space-x-2 text-gray-600">
                <Phone size={16} className="flex-shrink-0" />
                <span className="text-sm">
                  {(currentActiveBranch?.prefixCode !== "HQ" &&
                    currentActiveBranch?.contactNumber) ||
                    user.contactNumber}
                </span>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <Mail size={16} className="flex-shrink-0" />
                <span className="text-sm">
                  {(currentActiveBranch?.prefixCode !== "HQ" &&
                    currentActiveBranch?.email) ||
                    user.email}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <Building2 size={16} className="flex-shrink-0" />
                <span className="text-sm">
                  {currentActiveBranch &&
                  currentActiveBranch.prefixCode !== "HQ"
                    ? [
                        currentActiveBranch.addressLine0,
                        currentActiveBranch.addressLine1,
                        currentActiveBranch.addressLine2,
                        currentActiveBranch.cityName,
                      ]
                        .filter(Boolean)
                        .join(", ")
                    : [
                        user.addressLine0,
                        user.addressLine1,
                        user.addressLine2,
                        user.cityName,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
              </div>
            </div>
          </div>

          {invoiceDetails.consolidateGroup && (
            <Callout.Root color="bronze" role="alert">
              <div className="flex gap-2.5 items-center justify-center">
                <Callout.Icon>
                  <TriangleAlert />
                </Callout.Icon>
                <Callout.Text>
                  Invoice has been consolidated in
                  <Button
                    size="sm"
                    variant="link"
                    type="button"
                    className="px-1.5"
                    onClick={() => {
                      if (invoiceDetails.consolidateGroup) {
                        navigate(`/invoice-details/01A`, {
                          state: {
                            id: invoiceDetails.consolidateGroup.invoiceId,
                          },
                          replace: true,
                        });
                        window.location.reload(); //force refresh with new state
                      }
                    }}
                  >
                    {invoiceDetails.consolidateGroup.invoiceCodeNumber}
                  </Button>
                </Callout.Text>
              </div>
            </Callout.Root>
          )}

          {/* <Separator /> */}
          {eInvoiceDetail?.status == "Invalid" &&
            !invoiceDetails.isConsolidated && (
              <Callout.Root color="red" role="alert">
                {renderValidationErrors() ||
                  renderDetailErrors() ||
                  renderSingleError()}
              </Callout.Root>
            )}

          <Tabs.Root defaultValue="buyerOrSupplier">
            <Tabs.List color="gray" justify="start">
              <Tabs.Trigger value="buyerOrSupplier">
                {["11", "12", "13", "14"].includes(documentType)
                  ? "Supplier Information"
                  : "Buyer Information"}
                <span className="text-red-600">*</span>
              </Tabs.Trigger>
              <Tabs.Trigger value="invoiceDetail">Invoice Detail</Tabs.Trigger>
              <Tabs.Trigger value="frequentOfBilling">
                Frequent Of Billing
              </Tabs.Trigger>
              <Tabs.Trigger value="paymentAndPrepayment">
                Payment & Prepayment
              </Tabs.Trigger>
              <Tabs.Trigger value="shipping">Shipping</Tabs.Trigger>
              <Tabs.Trigger value="importAndExport">
                Import & Export
              </Tabs.Trigger>
            </Tabs.List>

            <Box pt="3">
              <Tabs.Content value="buyerOrSupplier">
                {/* Supplier Details Section */}
                {["11", "12", "13", "14"].includes(documentType) && (
                  <div className="space-y-4">
                    <div className="flex flex-row gap-2 items-center">
                      <h3 className="text-lg font-semibold">
                        Supplier Information{" "}
                      </h3>
                      <Button
                        disabled={isReadOnly}
                        onClick={() => setIsSearchSupplierModalOpen(true)}
                        type="button"
                        variant="outline"
                        size="sm"
                      >
                        Find
                        <Search />
                      </Button>
                      <SearchSupplierModal
                        isOpen={isSearchSupplierModalOpen}
                        onClose={() => setIsSearchSupplierModalOpen(false)}
                        onSelectSupplier={handleSelectSupplier}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="supplierId" required>
                          ID Type
                        </Label>
                        <Controller
                          name="supplierRegistrationType"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isReadOnly}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select type --" />
                              </SelectTrigger>
                              <SelectContent>
                                {registrationTypes.map((type) => (
                                  <SelectItem
                                    key={type.value}
                                    value={type.value}
                                  >
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.supplierRegistrationType && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierRegistrationType.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierRegistrationNumber" required>
                          Registration / Identification / Passport Number
                        </Label>
                        <Input
                          {...register("supplierRegistrationNumber")}
                          disabled={isReadOnly}
                        />
                        {errors.supplierRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierRegistrationNumber.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierTin" required>
                          TIN
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            {...register("supplierTin")}
                            disabled={isReadOnly}
                          />
                          <Button
                            variant="outline"
                            type="button"
                            onClick={() => setOpenTinSearch(true)}
                            disabled={isReadOnly}
                            className="group"
                          >
                            <Settings />
                          </Button>
                        </div>
                        {errors.supplierTin && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierTin.message}
                          </p>
                        )}
                      </div>
                      <TinSearch
                        isOpen={openTinSearch}
                        onClose={() => setOpenTinSearch(false)}
                        onSelect={(value) => setValue("supplierTin", value)}
                      />
                      {["12", "13", "14"].includes(documentType) && (
                        <div>
                          <Label htmlFor="originalInvoiceReference" required>
                            Original e-Invoice Reference Number (UUID)
                          </Label>
                          <div className="flex gap-1">
                            <Input
                              value={invoiceReferenceSubstitution}
                              disabled
                            />
                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => setOpenEInvoice(true)}
                              disabled={isReadOnly}
                            >
                              <Search />
                            </Button>
                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => setOpenInsertReference(true)}
                              disabled={isReadOnly}
                            >
                              <Plus />
                            </Button>
                          </div>
                          {errors?.originalInvoiceReference && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.originalInvoiceReference.message}
                            </p>
                          )}
                          <SearchInvoiceReferenceNumber
                            defaultChecked={defaultEInvoiceChecked}
                            isOpen={openEInvoice}
                            onClose={() => setOpenEInvoice(false)}
                            onSelect={(e) => handleSelectEInvoice(e)}
                            buyerTin={supplierTin}
                          />
                          <InsertEInvoiceReference
                            isOpen={openInsertReference}
                            onClose={() => setOpenInsertReference(false)}
                            onFilled={(uuid) => handleInvoiceReference(uuid)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="supplierName" required>
                          Name
                        </Label>
                        <Input
                          {...register("supplierName")}
                          disabled={isReadOnly}
                        />
                        {errors.supplierName && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierName.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierSstRegistrationNumber">
                          SST Registration Number
                        </Label>
                        <Input
                          {...register("supplierSstRegistrationNumber")}
                          disabled={isReadOnly}
                        />
                        {errors.supplierSstRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierSstRegistrationNumber.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierMsicCode" required>
                          MSIC Code
                        </Label>
                        <Input
                          {...register("supplierMsicCode")}
                          required
                          disabled={isReadOnly}
                        />
                        {errors.supplierMsicCode && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierMsicCode.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label
                          htmlFor="supplierBusinessActivityDescription"
                          required
                        >
                          Business Activity Description
                        </Label>
                        <Input
                          {...register("supplierBusinessActivityDescription")}
                          disabled={isReadOnly}
                        />
                        {errors.supplierBusinessActivityDescription && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierBusinessActivityDescription.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierTourismTaxRegistrationNumber">
                          Tourism Tax Registration Number
                        </Label>
                        <Input
                          {...register("supplierTourismTaxRegistrationNumber")}
                          disabled={isReadOnly}
                        />
                        {errors.supplierTourismTaxRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {
                              errors.supplierTourismTaxRegistrationNumber
                                .message
                            }
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierEmail" required>
                          Email
                        </Label>
                        <Input
                          {...register("supplierEmail")}
                          type="email"
                          disabled={isReadOnly}
                        />
                        {errors.supplierEmail && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierEmail.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierContact" required>
                          Contact Number
                        </Label>
                        <Input
                          {...register("supplierContact")}
                          disabled={isReadOnly}
                        />
                        {errors.supplierContact && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierContact.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="supplierAddressLine0" required>
                        Address Line 1
                      </Label>
                      <Input
                        {...register("supplierAddressLine0")}
                        disabled={isReadOnly}
                      />
                      {errors.supplierAddressLine0 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.supplierAddressLine0.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="supplierAddressLine1">
                        Address Line 2
                      </Label>
                      <Input
                        {...register("supplierAddressLine1")}
                        disabled={isReadOnly}
                      />
                      {errors.supplierAddressLine1 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.supplierAddressLine1.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="supplierAddressLine2">
                        Address Line 3
                      </Label>
                      <Input
                        {...register("supplierAddressLine2")}
                        disabled={isReadOnly}
                      />
                      {errors.supplierAddressLine2 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.supplierAddressLine2.message}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="supplierCity" required>
                          City
                        </Label>
                        <Input
                          {...register("supplierCity")}
                          disabled={isReadOnly}
                        />
                        {errors.supplierCity && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierCity.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierPostcode" required>
                          Postcode
                        </Label>
                        <Input
                          {...register("supplierPostcode")}
                          disabled={isReadOnly}
                        />
                        {errors.supplierPostcode && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierPostcode.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierCountry" required>
                          Country
                        </Label>
                        <Controller
                          name="supplierCountry"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isReadOnly}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select Country --" />
                              </SelectTrigger>
                              <SelectContent>
                                {countries.map((country) => (
                                  <SelectItem
                                    key={country.Code}
                                    value={country.Code}
                                  >
                                    {country.Country}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.supplierCountry && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierCountry.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="supplierState" required>
                          State
                        </Label>
                        <Controller
                          name="supplierState"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isReadOnly}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select State --" />
                              </SelectTrigger>
                              <SelectContent>
                                {states.map((state) => (
                                  <SelectItem
                                    key={state.Code}
                                    value={state.Code}
                                  >
                                    {state.State}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.supplierState && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.supplierState.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Buyer Details Section */}
                {["01", "02", "03", "04", "01A"].includes(documentType) && (
                  <div className="space-y-4">
                    <div className="flex flex-row gap-2 items-center">
                      <h3 className="text-lg font-semibold">
                        Buyer Information{" "}
                      </h3>
                      <Button
                        disabled={isReadOnly || documentType == "01A"}
                        onClick={() => setIsSearchBuyerModalOpen(true)}
                        type="button"
                        variant="outline"
                        size="sm"
                      >
                        Find
                        <Search />
                      </Button>
                    </div>
                    {documentType !== "01A" && (
                      <SearchBuyerModal
                        isOpen={isSearchBuyerModalOpen}
                        onClose={() => setIsSearchBuyerModalOpen(false)}
                        onSelectBuyer={handleSelectBuyer}
                      />
                    )}

                    {buyerCode && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">
                          Customer Code:
                        </span>
                        <span className="font-mono bg-muted px-2 py-0.5 rounded text-foreground">
                          {buyerCode}
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="buyerRegistrationType" required>
                          ID Type
                        </Label>
                        <Controller
                          name="buyerRegistrationType"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isReadOnly || documentType === "01A"}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select type --" />
                              </SelectTrigger>
                              <SelectContent>
                                {registrationTypes.map((type) => (
                                  <SelectItem
                                    key={type.value}
                                    value={type.value}
                                  >
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.buyerRegistrationType && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerRegistrationType.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerRegistrationNumber" required>
                          Registration / Identification / Passport Number
                        </Label>
                        <Input
                          {...register("buyerRegistrationNumber")}
                          disabled={isReadOnly || documentType === "01A"}
                        />
                        {errors.buyerRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerRegistrationNumber.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerTin" required>
                          TIN
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            {...register("buyerTin")}
                            disabled={isReadOnly || documentType === "01A"}
                          />
                          <Button
                            variant="outline"
                            type="button"
                            onClick={() => setOpenTinSearch(true)}
                            disabled={isReadOnly || documentType == "01A"}
                            className="group"
                          >
                            <Settings />
                          </Button>
                        </div>
                        {errors.buyerTin && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerTin.message}
                          </p>
                        )}
                      </div>
                      <TinSearch
                        isOpen={openTinSearch}
                        onClose={() => setOpenTinSearch(false)}
                        onSelect={(value) => setValue("buyerTin", value)}
                      />
                      {["02", "03", "04"].includes(documentType) && (
                        <div>
                          <Label htmlFor="originalInvoiceReference" required>
                            Original e-Invoice Reference Number (UUID)
                          </Label>
                          <div className="flex gap-1">
                            <Input
                              value={invoiceReferenceSubstitution}
                              disabled
                            />
                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => setOpenEInvoice(true)}
                              disabled={isReadOnly}
                            >
                              <Search />
                            </Button>
                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => setOpenInsertReference(true)}
                              disabled={isReadOnly}
                            >
                              <Plus />
                            </Button>
                          </div>
                          {errors?.originalInvoiceReference && (
                            <p className="text-red-600 text-sm mt-1">
                              {errors.originalInvoiceReference.message}
                            </p>
                          )}
                          <SearchInvoiceReferenceNumber
                            defaultChecked={defaultEInvoiceChecked}
                            isOpen={openEInvoice}
                            onClose={() => setOpenEInvoice(false)}
                            onSelect={(e) => handleSelectEInvoice(e)}
                            buyerTin={buyerTin}
                          />
                          <InsertEInvoiceReference
                            isOpen={openInsertReference}
                            onClose={() => setOpenInsertReference(false)}
                            onFilled={(uuid) => handleInvoiceReference(uuid)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="buyerName" required>
                          Name
                        </Label>
                        <Input
                          {...register("buyerName")}
                          disabled={isReadOnly || documentType === "01A"}
                        />
                        {errors.buyerName && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerName.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerSstRegistrationNumber">
                          SST Registration Number
                        </Label>
                        <Input
                          {...register("buyerSstRegistrationNumber")}
                          disabled={isReadOnly || documentType === "01A"}
                        />
                        {errors.buyerSstRegistrationNumber && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerSstRegistrationNumber.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerEmail" required>
                          Email
                        </Label>
                        <Input
                          {...register("buyerEmail")}
                          type="email"
                          disabled={isReadOnly || documentType === "01A"}
                        />
                        {errors.buyerEmail && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerEmail.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerContact" required>
                          Contact Number
                        </Label>
                        <Input
                          {...register("buyerContact")}
                          disabled={isReadOnly || documentType === "01A"}
                        />
                        {errors.buyerContact && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerContact.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="buyerAddressLine0" required>
                        Address Line 1
                      </Label>
                      <Input
                        {...register("buyerAddressLine0")}
                        disabled={isReadOnly || documentType === "01A"}
                      />
                      {errors.buyerAddressLine0 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerAddressLine0.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="buyerAddressLine1">Address Line 2</Label>
                      <Input
                        {...register("buyerAddressLine1")}
                        disabled={isReadOnly || documentType === "01A"}
                      />
                      {errors.buyerAddressLine1 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerAddressLine1.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="buyerAddressLine2">Address Line 3</Label>
                      <Input
                        {...register("buyerAddressLine2")}
                        disabled={isReadOnly || documentType === "01A"}
                      />
                      {errors.buyerAddressLine2 && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.buyerAddressLine2.message}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="buyerCity" required>
                          City
                        </Label>
                        <Input
                          {...register("buyerCity")}
                          disabled={isReadOnly || documentType === "01A"}
                        />
                        {errors.buyerCity && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerCity.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerPostcode" required>
                          Postcode
                        </Label>
                        <Input
                          {...register("buyerPostcode")}
                          disabled={isReadOnly || documentType === "01A"}
                        />
                        {errors.buyerPostcode && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerPostcode.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerCountry" required>
                          Country
                        </Label>
                        <Controller
                          name="buyerCountry"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isReadOnly || documentType === "01A"}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select Country --" />
                              </SelectTrigger>
                              <SelectContent>
                                {countries.map((country) => (
                                  <SelectItem
                                    key={country.Code}
                                    value={country.Code}
                                  >
                                    {country.Country}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.buyerCountry && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerCountry.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="buyerState" required>
                          State
                        </Label>
                        <Controller
                          name="buyerState"
                          control={control}
                          render={({ field }) => (
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isReadOnly || documentType === "01A"}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="-- Select State --" />
                              </SelectTrigger>
                              <SelectContent>
                                {states.map((state) => (
                                  <SelectItem
                                    key={state.Code}
                                    value={state.Code}
                                  >
                                    {state.State}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.buyerState && (
                          <p className="text-red-600 text-sm mt-1">
                            {errors.buyerState.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Tabs.Content>

              <Tabs.Content value="invoiceDetail">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Invoice Detail</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="invoiceDate">Issuance Datetime</Label>
                      <Input
                        id="invoiceDate"
                        type="datetime-local"
                        {...register("invoiceDate")}
                      />
                      {errors.invoiceDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.invoiceDate.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="codeNumber">Invoice Number</Label>
                      <Input
                        id="codeNumber"
                        {...register("codeNumber")}
                        disabled
                      />
                    </div>
                    <div>
                      <Label htmlFor="docDate">Invoice Date</Label>
                      <Input
                        type="date"
                        {...register("docDate")}
                        disabled={isReadOnly}
                      />
                      {errors.docDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.docDate.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="jobCode">Job Code</Label>
                      <Controller
                        name="jobCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? undefined}
                            disabled={documentType === "01A" || isReadOnly}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue
                                placeholder={
                                  jobCode
                                    ? "-- Select job code --"
                                    : "No job code available"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {jobCode &&
                                Array.isArray(jobCode) &&
                                jobCode.map((item) => (
                                  <SelectItem key={item.code} value={item.code}>
                                    {item.code}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.jobCode && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.jobCode.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="invoiceCurrencyCode">
                        Invoice Currency Code
                      </Label>
                      <Controller
                        name="currencyCode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                            disabled={documentType === "01A" || isReadOnly}
                            onOpenChange={(open) => {
                              if (!open) setCurrencySearch("");
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              <div className="px-2 py-1.5 sticky top-0 bg-popover z-10">
                                <Input
                                  placeholder="Search currency..."
                                  value={currencySearch}
                                  onChange={(e) =>
                                    setCurrencySearch(e.target.value)
                                  }
                                  onKeyDown={(e) => e.stopPropagation()}
                                  className="h-8 text-sm"
                                />
                              </div>
                              {(() => {
                                const q = currencySearch.toLowerCase();
                                const filtered = currencyCodes.filter(
                                  (c) =>
                                    c.Code.toLowerCase().includes(q) ||
                                    c.Currency.toLowerCase().includes(q) ||
                                    c.Symbol.toLowerCase().includes(q),
                                );
                                const myr = filtered.find(
                                  (c) => c.Code === "MYR",
                                );
                                const rest = filtered.filter(
                                  (c) => c.Code !== "MYR",
                                );
                                return [...(myr ? [myr] : []), ...rest].map(
                                  (currency) => (
                                    <SelectItem
                                      key={currency.Code}
                                      value={currency.Code}
                                    >
                                      {currency.Code} — {currency.Currency}
                                    </SelectItem>
                                  ),
                                );
                              })()}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.currencyCode && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.currencyCode.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="currencyExchangeRate">
                        Currency Exchange Rate
                      </Label>
                      <Input
                        type="number"
                        step="0.0001"
                        disabled={documentType === "01A" || isReadOnly}
                        {...register("currencyExchangeRate", {
                          valueAsNumber: true,
                        })}
                      />
                      {errors.currencyExchangeRate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.currencyExchangeRate.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="originalInvoiceNumber">
                        POS Reference Number (If applicable)
                      </Label>
                      <Input
                        {...register("originalInvoiceNumber")}
                        disabled={isReadOnly}
                      />
                      {errors.originalInvoiceNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.originalInvoiceNumber.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Frequent of Billing Information (all optional) */}
              <Tabs.Content value="frequentOfBilling">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    Frequent of Billing Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="frequencyOfBilling">
                        Frequency Of Billing
                      </Label>
                      <Controller
                        name="frequencyOfBilling"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? undefined}
                            disabled={isReadOnly || documentType == "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {frequencyOfBilling.map((frequency) => (
                                <SelectItem
                                  key={frequency.value}
                                  value={frequency.value}
                                >
                                  {frequency.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.frequencyOfBilling && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.frequencyOfBilling.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="billingPeriodStartDate">
                        Billing Period Start Date
                      </Label>
                      <Input
                        type="date"
                        {...register("billingPeriodStartDate")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                      {errors.billingPeriodStartDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.billingPeriodStartDate.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="billingPeriodEndDate">
                        Billing Period End Date
                      </Label>
                      <Input
                        type="date"
                        {...register("billingPeriodEndDate")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                      {errors.billingPeriodEndDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.billingPeriodEndDate.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Payment and Prepayment Information (all optional) */}
              <Tabs.Content value="paymentAndPrepayment">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    Payment and Prepayment Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="paymentMode">Payment Mode</Label>
                      <Controller
                        name="paymentMode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? undefined}
                            disabled={isReadOnly || documentType == "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {paymentMode.map((payment) => (
                                <SelectItem
                                  key={payment.Code}
                                  value={payment.Code}
                                >
                                  {payment["Payment Method"]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.paymentMode && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.paymentMode.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="supplierBankAccountNumber">
                        Supplier's Bank Account Number
                      </Label>
                      <Input
                        {...register("supplierBankAccountNumber")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                      {errors.supplierBankAccountNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.supplierBankAccountNumber.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="paymentTerms">Payment Terms</Label>
                      <Input
                        {...register("paymentTerms")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                      {errors.paymentTerms && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.paymentTerms.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="prepaymentAmount">
                        Prepayment Amount (RM)
                      </Label>
                      <Input
                        {...register("prepaymentAmount", {
                          valueAsNumber: true,
                        })}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                      {errors.prepaymentAmount && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.prepaymentAmount.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="prepaymentDate">Prepayment Date</Label>
                      <Input
                        type="date"
                        {...register("prepaymentDate")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                      {errors.prepaymentDate && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.prepaymentDate.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="prepaymentReferenceNumber">
                        Prepayment Reference Number
                      </Label>
                      <Input
                        {...register("prepaymentReferenceNumber")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                      {errors.prepaymentReferenceNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.prepaymentReferenceNumber.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="billReferenceNumber">
                        Bill Reference Number
                      </Label>
                      <Input
                        {...register("billReferenceNumber")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                      {errors.billReferenceNumber && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors.billReferenceNumber.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Shipping Information (all optional) */}
              {/* Applicable to transactions where goods are shipped to a different recipient and/or address (i.e., different from the Buyer's details) */}
              <Tabs.Content value="shipping">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">
                      Shipping Information
                    </h3>
                    <h5 className="text-sm font-light">
                      Applicable to transactions where goods are shipped to a
                      different recipient and/or address (i.e., different from
                      the Buyer's details)
                    </h5>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="shipping.recipientName">
                        Shipping Recipient's Name
                      </Label>
                      <Input
                        {...register("shipping.recipientName")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine0">
                        Address Line 1
                      </Label>
                      <Input
                        {...register("shipping.recipientAddressLine0")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine1">
                        Address Line 2
                      </Label>
                      <Input
                        {...register("shipping.recipientAddressLine1")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientAddressLine2">
                        Address Line 3
                      </Label>
                      <Input
                        {...register("shipping.recipientAddressLine2")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientPostcode">
                        Postcode
                      </Label>
                      <Input
                        {...register("shipping.recipientPostcode")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientCity">City</Label>
                      <Input
                        {...register("shipping.recipientCity")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientCountry">Country</Label>
                      <Controller
                        name="shipping.recipientCountry"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value ?? undefined}
                            onValueChange={field.onChange}
                            disabled={isReadOnly || documentType == "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map((country) => (
                                <SelectItem
                                  key={country.Code}
                                  value={country.Code}
                                >
                                  {country.Country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientState">State</Label>
                      <Controller
                        name="shipping.recipientState"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? undefined}
                            disabled={isReadOnly || documentType == "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {states.map((state) => (
                                <SelectItem key={state.Code} value={state.Code}>
                                  {state.State}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientRegistrationType">
                        ID Type
                      </Label>
                      <Controller
                        name="shipping.recipientRegistrationType"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value ?? undefined}
                            onValueChange={field.onChange}
                            disabled={isReadOnly || documentType == "01A"}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="-- Select type --" />
                            </SelectTrigger>
                            <SelectContent>
                              {registrationTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientRegistrationNumber">
                        Registration / Identification / Passport Number
                      </Label>
                      <Input
                        {...register("shipping.recipientRegistrationNumber")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="shipping.recipientTin">
                        Shipping Recipient's TIN
                      </Label>
                      <Input
                        {...register("shipping.recipientTin")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* Import And Export Information (optional except Reference Number of Customs Form No.1,9 etc. is mandatory where applicable) */}
              {/* Applicable to transactions where goods are shipped to a different recipient and/or address (i.e., different from the Buyer's details) */}
              <Tabs.Content value="importAndExport">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">
                      Import And Export Information
                    </h3>
                    <h5 className="text-sm font-light">
                      Applicable to import and export of goods
                    </h5>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customs.formReferenceNumber1">
                        Reference Number of Customs Form No.1,9 etc.
                      </Label>
                      <Input
                        {...register("customs.formReferenceNumber1")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.incoterms">Incoterms</Label>
                      <Input
                        {...register("customs.incoterms")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.ftaInformation">
                        Free Trade Agreement (FTA) Information
                      </Label>
                      <Input
                        {...register("customs.ftaInformation")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.authorisationNumberForCertifiedExporter">
                        Authorization Number for Certified Exporter (e.g., ATIGA
                        number)
                      </Label>
                      <Input
                        {...register(
                          "customs.authorisationNumberForCertifiedExporter",
                        )}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.formReferenceNumber2">
                        Reference Number of Custom Form No.2
                      </Label>
                      <Input
                        {...register("customs.formReferenceNumber2")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="customs.detailsOfOtherCharges">
                        Details of Other Charges
                      </Label>
                      <Input
                        {...register("customs.detailsOfOtherCharges")}
                        disabled={isReadOnly || documentType == "01A"}
                      />
                    </div>
                  </div>
                </div>
              </Tabs.Content>
            </Box>
          </Tabs.Root>

          {/* Items Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Items</h3>
              {!isReadOnly && ["01", "11", "01A"].includes(documentType) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSearchModalOpen(true)}
                  className="shadow"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {documentType !== "01A" ? "Add Item" : "Add Invoice"}
                </Button>
              )}
            </div>

            <SearchModal
              isOpen={isSearchModalOpen && documentType !== "01A"}
              onClose={() => setIsSearchModalOpen(false)}
              onSelectItem={handleSelectItem}
            />

            <SearchInvoiceModal
              isOpen={isSearchModalOpen && documentType === "01A"}
              onClose={() => setIsSearchModalOpen(false)}
              onSelectInvoice={(e) => {
                handleConsolidateInvoice(e);
              }}
            />

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-36">Classification</TableHead>
                  <TableHead className="min-w-36">Description</TableHead>
                  <TableHead className="min-w-36">Unit Price</TableHead>
                  <TableHead className="min-w-36">Quantity</TableHead>
                  <TableHead className="min-w-36">Tax Type</TableHead>
                  <TableHead className="min-w-36">Tax Rate</TableHead>
                  <TableHead className="min-w-36">Tax Amount</TableHead>
                  <TableHead className="min-w-36">
                    Details of Tax Exemption
                  </TableHead>
                  <TableHead className="min-w-36">
                    Amount Exempted from Tax
                  </TableHead>
                  {/* <TableHead className="min-w-36">Subtotal</TableHead> */}
                  {/* <TableHead className="min-w-36">Total Excluding Tax</TableHead> */}
                  <TableHead className="min-w-36">Measurement</TableHead>
                  <TableHead className="min-w-36">Discount Rate</TableHead>
                  {/* <TableHead className="min-w-36">Discount Amount</TableHead> */}
                  <TableHead className="min-w-36">Fee / Charge Rate</TableHead>
                  {/* <TableHead className="min-w-36">Fee / Charge Amount</TableHead> */}
                  <TableHead className="min-w-36">
                    Product Tariff Code
                  </TableHead>
                  <TableHead className="min-w-36">Country of Origin</TableHead>
                  <TableHead className="min-w-36">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map(
                  (field, index) =>
                    (!items[index]?._delete || !items[index]) && (
                      <TableRow key={field.id}>
                        <TableCell>
                          <Controller
                            name={`items.${index}.classification`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={isReadOnly || documentType === "01A"}
                              >
                                <SelectTrigger className="w-64 text-left">
                                  <SelectValue placeholder="-- Select Classification --" />
                                </SelectTrigger>
                                <SelectContent className="w-96">
                                  {classificationCodes.map((code) => (
                                    <SelectItem
                                      key={code.Code}
                                      value={code.Code}
                                    >
                                      {code.Code} - {code.Description}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="w-72"
                            {...register(`items.${index}.description`)}
                            readOnly={isReadOnly}
                            disabled={documentType === "01A" || isReadOnly}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={step}
                            {...register(`items.${index}.unitPrice`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "unitPrice",
                                e.target.value,
                              )
                            }
                            readOnly={isReadOnly}
                            disabled={documentType === "01A" || isReadOnly}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="any"
                            {...register(`items.${index}.quantity`, {
                              valueAsNumber: true,
                            })}
                            className="w-20"
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "quantity",
                                e.target.value,
                              )
                            }
                            disabled={isReadOnly || documentType === "01A"}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`items.${index}.taxType`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={isReadOnly || documentType === "01A"}
                              >
                                <SelectTrigger className="text-left w-56">
                                  <SelectValue placeholder="-- Select tax type --" />
                                </SelectTrigger>
                                <SelectContent className="">
                                  {taxTypes.map((taxType) => (
                                    <SelectItem
                                      key={taxType.Code}
                                      value={taxType.Code}
                                    >
                                      {taxType.Code} - {taxType.Description}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            {...register(`items.${index}.taxRate`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "taxRate",
                                e.target.value,
                              )
                            }
                            disabled={isReadOnly || documentType === "01A"}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={step}
                            {...register(`items.${index}.taxAmount`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "taxAmount",
                                e.target.value,
                              )
                            }
                            disabled={isReadOnly || documentType === "01A"}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            {...register(
                              `items.${index}.detailsOfTaxExemption`,
                            )}
                            disabled={isReadOnly || documentType === "01A"}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            {...register(
                              `items.${index}.amountExemptedFromTax`,
                              { valueAsNumber: true },
                            )}
                            disabled={isReadOnly || documentType === "01A"}
                          />
                        </TableCell>
                        {/* <TableCell>
                        <Input
                          type="number"
                          {...register(`items.${index}.subtotal`)}
                          className="w-20"
                          disabled
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          {...register(`items.${index}.totalExcludingTax`)}
                          className="w-24"
                          disabled
                        />
                      </TableCell> */}
                        <TableCell>
                          <Controller
                            control={control}
                            name={`items.${index}.measurement`}
                            render={({ field }) => (
                              <UomCombobox
                                value={field.value}
                                onChange={field.onChange}
                                disabled={isReadOnly || documentType === "01A"}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step={RATE_INPUT_STEP}
                            {...register(`items.${index}.discountRate`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "discountRate",
                                e.target.value,
                              )
                            }
                            className="w-20"
                            disabled={isReadOnly || documentType === "01A"}
                          />
                        </TableCell>
                        {/* <TableCell>
                        <Input
                          type="number"
                          step={step}
                          {...register(`items.${index}.discountAmount`)}
                          onChange={(e) => handleCalculation(index, "discountAmount", e.target.value)}
                          className="w-20"
                          disabled
                        />
                      </TableCell> */}
                        <TableCell>
                          <Input
                            type="number"
                            step={RATE_INPUT_STEP}
                            {...register(`items.${index}.feeRate`, {
                              valueAsNumber: true,
                            })}
                            onChange={(e) =>
                              handleCalculation(
                                index,
                                "feeRate",
                                e.target.value,
                              )
                            }
                            className="w-20"
                            disabled={isReadOnly || documentType === "01A"}
                          />
                        </TableCell>
                        {/* <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          disabled
                          {...register(`items.${index}.feeAmount`)}
                          onChange={(e) => handleCalculation(index, "feeAmount", e.target.value)}
                          className="w-20"
                        />
                      </TableCell> */}
                        <TableCell>
                          <Input
                            {...register(`items.${index}.productTariffCode`)}
                            className="w-20"
                            disabled={isReadOnly || documentType === "01A"}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`items.${index}.countryOfOrigin`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                                disabled={isReadOnly || documentType === "01A"}
                              >
                                <SelectTrigger className="w-52 text-left">
                                  <SelectValue placeholder="-- Select Country --" />
                                </SelectTrigger>
                                <SelectContent>
                                  {countries.map((country) => (
                                    <SelectItem
                                      key={country.Code}
                                      value={country.Code}
                                    >
                                      {country.Country}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </TableCell>
                        <TableCell className="">
                          <Input
                            type="number"
                            {...register(`items.${index}.total`)}
                            disabled
                            className="border-0"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleRemoveItem(index, documentType)
                            }
                            className="p-0 h-auto"
                            disabled={isReadOnly}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ),
                )}
                {!["01", "11", "01A"].includes(documentType) && !isReadOnly && (
                  <TableRow>
                    <TableCell>
                      <Select
                        value={newLineItem.classification}
                        onValueChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            classification: e,
                          }))
                        }
                      >
                        <SelectTrigger className="w-64 text-left">
                          <SelectValue placeholder="-- Select Classification --" />
                        </SelectTrigger>
                        <SelectContent className="w-96">
                          {classificationCodes.map((code) => (
                            <SelectItem key={code.Code} value={code.Code}>
                              {code.Code} - {code.Description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        className="w-72"
                        value={newLineItem.description}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={newLineItem.unitPrice}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            unitPrice: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        className="w-20"
                        value={newLineItem.quantity}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            quantity: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        onValueChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            taxType: e as
                              | "01"
                              | "02"
                              | "03"
                              | "04"
                              | "05"
                              | "06"
                              | "E",
                          }))
                        }
                        value={newLineItem.taxType}
                      >
                        <SelectTrigger className="text-left w-56">
                          <SelectValue placeholder="-- Select tax type --" />
                        </SelectTrigger>
                        <SelectContent className="">
                          {taxTypes.map((taxType) => (
                            <SelectItem key={taxType.Code} value={taxType.Code}>
                              {taxType.Code} - {taxType.Description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={newLineItem.taxRate}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            taxRate: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={newLineItem.taxAmount}
                        // disabled
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            taxAmount: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={newLineItem.detailsOfTaxExemption ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            detailsOfTaxExemption: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={newLineItem.amountExemptedFromTax ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            amountExemptedFromTax: Number(e.target.value),
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <UomCombobox
                        value={newLineItem.measurement}
                        onChange={(code) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            measurement: code,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={newLineItem.discountRate ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            discountRate: Number(e.target.value),
                          }))
                        }
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={newLineItem.feeRate ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            feeRate: Number(e.target.value),
                          }))
                        }
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={newLineItem.productTariffCode ?? undefined}
                        onChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            productTariffCode: e.target.value,
                          }))
                        }
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={newLineItem.countryOfOrigin}
                        onValueChange={(e) =>
                          setNewLineItem((prev) => ({
                            ...prev,
                            countryOfOrigin: e,
                          }))
                        }
                      >
                        <SelectTrigger className="w-52 text-left">
                          <SelectValue placeholder="-- Select Country --" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.Code} value={country.Code}>
                              {country.Country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="">
                      <Input
                        value={newLineItem.total}
                        disabled
                        className="border-0"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleAddLineItem}
                        className="p-0 h-auto"
                      >
                        <Plus className="text-black dark:text-white" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Totals */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Total Excluding Tax:</span>
                <span>
                  {
                    currencyCodes.find(
                      (currency) => currency.Code == currencyCode,
                    )?.Symbol
                  }{" "}
                  {formatTotalAmount(totals?.totalExcludingTax ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Including Tax:</span>
                <span>
                  {
                    currencyCodes.find(
                      (currency) => currency.Code == currencyCode,
                    )?.Symbol
                  }{" "}
                  {formatTotalAmount(totals?.totalIncludingTax ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Tax Amount:</span>
                <span>
                  {
                    currencyCodes.find(
                      (currency) => currency.Code == currencyCode,
                    )?.Symbol
                  }{" "}
                  {formatTotalAmount(totals?.totalTaxAmount ?? 0)}
                </span>
              </div>
              {/* Invoice Additional Discount Amount: hidden input, display read-only when non-zero */}
              <input
                type="hidden"
                {...register("invoiceAdditionalDiscountAmount", {
                  setValueAs: (value) => Number(value),
                })}
              />
              {!!Number(watch("invoiceAdditionalDiscountAmount")) && (
                <div className="flex justify-between">
                  <span className="content-center">
                    Invoice Additional Discount Amount:
                  </span>
                  <span>
                    {
                      currencyCodes.find(
                        (currency) => currency.Code == currencyCode,
                      )?.Symbol
                    }{" "}
                    {formatDecimal(
                      Number(watch("invoiceAdditionalDiscountAmount")),
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="content-center">
                  Invoice Additional Fee Amount:
                </span>
                <span>
                  <Input
                    className="text-right h-8"
                    disabled={documentType == "01A"}
                    {...register("invoiceAdditionalFeeAmount", {
                      setValueAs: (value) => Number(value),
                    })}
                  />
                  {errors.invoiceAdditionalFeeAmount && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.invoiceAdditionalFeeAmount.message?.toString()}
                    </p>
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="content-center">Rounding Amount:</span>
                <span>
                  <Input
                    className="text-right h-8"
                    {...register("roundingAmount", {
                      setValueAs: (value) => Number(value),
                    })}
                    disabled={isReadOnly || documentType == "01A"}
                  />
                  {errors.roundingAmount && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.roundingAmount.message?.toString()}
                    </p>
                  )}
                </span>
              </div>
              {/* <div className="flex justify-between">
                <span>Total Tax Amount Per Tax Type:</span>
                <span>RM {formatTotalAmount(totals?.totalTaxAmount ?? 0)}</span>
              </div> */}
              <Separator />
              <div className="flex justify-between font-bold">
                <span>Total Payable Amount:</span>
                <span>
                  {
                    currencyCodes.find(
                      (currency) => currency.Code == currencyCode,
                    )?.Symbol
                  }{" "}
                  {formatTotalAmount(totals?.totalPayableAmount ?? 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end pt-2 gap-3">
            <Button
              onClick={handlePrint}
              type="button"
              variant="outline"
              className="w-full md:w-auto"
              disabled={isPrinting || isUpdatingInvoice}
            >
              {isPrinting ? (
                <>
                  <Loader2 size={10} className="animate-spin" />
                  Printing...
                </>
              ) : (
                "Print"
              )}
            </Button>
            {/* No manual "Sync Status" action by design. Status reconciliation
                is automatic: the background discovery pass picks up MyTax
                cancellations, and cancelling always re-checks LHDN first, so a
                user never has to ask for a refresh to stay correct. */}
            {(eInvoiceDetails.data == null ||
              (eInvoiceDetails?.data[0].status != "Valid" &&
                eInvoiceDetails.data[0]?.status != "Submitted" &&
                eInvoiceDetails.data[0]?.status != "Cancelled")) && (
              <Button
                type="submit"
                disabled={isUpdatingInvoice || isPrinting}
                className="w-full md:w-auto"
              >
                {isUpdatingInvoice ? (
                  <>
                    <Loader2 size={10} className="animate-spin" />
                    Saving
                  </>
                ) : (
                  "Save Invoice"
                )}
              </Button>
            )}
            {/* Delete button for consolidated invoices */}
            {documentType === "01A" && eInvoiceDetails.data == null && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    className="w-full md:w-auto"
                  >
                    Delete Consolidated Invoice
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Delete Consolidated Invoice
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this consolidated invoice?
                      This will revert all individual invoices back to draft
                      status, making them available for consolidation again.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteConsolidatedInvoice}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {eInvoiceDetails.data !== null &&
              eInvoiceDetails?.data[0].status == "Valid" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      // onClick={handleCancel}
                      type="button"
                      disabled={isUpdatingInvoice || isCancelling}
                      className="w-full md:w-auto"
                    >
                      {isCancelling ? (
                        <>
                          <Loader2 size={10} className="animate-spin" />
                          Cancelling...
                        </>
                      ) : (
                        "Cancel Invoice"
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Invoice</AlertDialogTitle>
                      <AlertDialogDescription>
                        Why would you want to cancel invoice{" "}
                        {eInvoiceDetails.data[0].uuid}?
                      </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="my-4">
                      <label className="block mb-1 text-sm font-medium">
                        Cancellation Reason
                      </label>
                      <textarea
                        className="w-full rounded border p-2 text-sm"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Enter reason..."
                        rows={3}
                      />
                    </div>

                    <AlertDialogFooter>
                      <AlertDialogCancel>Back</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleCancel}
                        disabled={!cancelReason.trim()} // disable if reason is empty
                      >
                        Cancel Invoice
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const InvoiceNotFound = () => {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-gray-400">
      <SearchX size={150} />
      <p>Invoice not found.</p>
    </div>
  );
};

export default EInvoiceWithDetailsForm;
